import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ETAPAS, COR_ORIGEM } from '../constantes';

// Aba "Oportunidade" da ficha do cliente.
// Lista as negociações presas a este cadastro (cliente_id) — a que virou o
// cliente e as que nasceram aqui — e cria oportunidade nova já vinculada:
// origem 'Carteira', nome e telefone da ficha, sem redigitar nada. A
// oportunidade aparece no CRM e na listagem (Alt+L) como qualquer outra.
// Qualquer usuário pode criar (decisão do Vagner, 27/09/2026).

const token = () => localStorage.getItem('token');
const cab = { headers: { Authorization: `Bearer ${token()}` } };
const inp = { width: '100%', padding: '3px 6px', borderRadius: 3, border: '1px solid #b0b8c4', fontSize: 13, color: '#111', boxSizing: 'border-box', background: '#fff', height: 26 };
const lbl = { fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 3 };
const th = { textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', padding: '6px 8px', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' };
const td = { fontSize: 12.5, padding: '6px 8px', borderBottom: '1px solid #f3f4f6', verticalAlign: 'middle' };

const fmtData = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('pt-BR');
};
const moeda = (n) => `R$ ${(Number(n) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
const valorNum = (v) => typeof v === 'number' ? v : (parseFloat(String(v || '').replace(/\./g, '').replace(',', '.')) || 0);
// Total da venda gravada na oportunidade (snapshot do Gravar Cliente)
function totalVenda(op) {
  const v = op?.venda;
  if (!v) return 0;
  if (Array.isArray(v.itens) && v.itens.length) {
    return v.itens.reduce((s, it) => s + Math.max(1, parseInt(it.quantidade) || 1) * valorNum(it.valor), 0);
  }
  return valorNum(v.valor);
}

export default function OportunidadesCliente({ clienteId, nome, telefone, consultor }) {
  const navigate = useNavigate();
  const [lista, setLista] = useState(null);
  const [erro, setErro] = useState('');
  const [closers, setClosers] = useState([]);
  const [abrindo, setAbrindo] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const nomeLogado = localStorage.getItem('nomeUsuario') || '';
  const [form, setForm] = useState({ closer: '', etapa: 'lead', observacao: '' });

  const carregar = useCallback(() => {
    if (!clienteId) { setLista([]); return; }
    fetch(`/oportunidades?cliente_id=${encodeURIComponent(clienteId)}`, cab)
      .then(r => r.ok ? r.json() : Promise.reject(new Error('Falha ao carregar as oportunidades')))
      .then(arr => setLista(Array.isArray(arr) ? arr : []))
      .catch(e => { setErro(e.message); setLista([]); });
  }, [clienteId]);
  useEffect(() => { carregar(); }, [carregar]);

  // Closers de verdade (o backend exige cargo Closer quando quem lança é SDR)
  useEffect(() => {
    fetch('/usuarios', cab).then(r => r.ok ? r.json() : []).then(arr => {
      const cs = (Array.isArray(arr) ? arr : [])
        .filter(u => (u.cargo || '').toLowerCase().includes('closer') && u.email !== 'clau@preservamarcas.com.br')
        .map(u => u.nome).sort((a, b) => a.localeCompare(b));
      setClosers(cs);
      // Closer sugerido: o consultor da ficha se for closer; senão quem está logado, se for closer
      setForm(f => f.closer ? f : { ...f, closer: cs.includes(consultor) ? consultor : (cs.includes(nomeLogado) ? nomeLogado : '') });
    }).catch(() => {});
  }, [consultor, nomeLogado]);

  async function criar(e) {
    if (e && e.preventDefault) e.preventDefault();
    if (!(form.closer || '').trim()) return alert('Selecione o closer que vai atender.');
    setSalvando(true);
    try {
      const res = await fetch('/oportunidades', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({
          cliente_id: clienteId, nome_cliente: nome, telefone,
          closer: form.closer, etapa: form.etapa, observacao: form.observacao,
          sdr: nomeLogado,
        }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.erro || 'Não foi possível criar a oportunidade');
      setAbrindo(false);
      setForm(f => ({ ...f, etapa: 'lead', observacao: '' }));
      carregar();
    } catch (err) { alert(err.message); }
    finally { setSalvando(false); }
  }

  if (!clienteId) {
    return (
      <div style={{ background: '#fff', border: '1px solid #c8d0da', borderTop: 'none', padding: 24, fontSize: 13, color: '#6b7280', textAlign: 'center' }}>
        Cliente novo entra só pela oportunidade no CRM. Esta aba serve para quem já está cadastrado: abra a ficha de um cliente e crie aqui os negócios novos dele.
      </div>
    );
  }

  const etapaDe = (k) => ETAPAS.find(e => e.key === k) || { label: k || '—', cor: '#6b7280', bg: '#f9fafb' };
  const abertas = (lista || []).filter(o => !['ganho', 'perdido', 'sem-interacao'].includes(o.etapa)).length;

  return (
    <div style={{ background: '#fff', border: '1px solid #c8d0da', borderTop: 'none', padding: '10px 14px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 12.5, color: '#374151' }}>
          Negócios deste cliente: <b>{lista ? lista.length : '…'}</b>
          {lista && lista.length > 0 && <span style={{ color: '#6b7280' }}> · em andamento: <b style={{ color: '#d97706' }}>{abertas}</b></span>}
          <span style={{ marginLeft: 10, fontSize: 11, color: '#9ca3af' }}>Criadas aqui nascem com origem <b style={{ color: COR_ORIGEM.Carteira }}>Carteira</b> e aparecem no CRM e na listagem (Alt+L).</span>
        </div>
        {!abrindo && (
          <button type="button" onClick={() => setAbrindo(true)}
            style={{ background: '#0a1628', color: '#fff', border: 'none', borderRadius: 4, padding: '5px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            + Nova Oportunidade
          </button>
        )}
      </div>

      {/* div, não <form>: a aba vive dentro do <form> da ficha e formulário
          aninhado entrega o submit pro de fora (o navegador descarta o interno) */}
      {abrindo && (
        <div style={{ border: '2px solid #0a1628', borderRadius: 6, padding: 14, marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#0a1628', textTransform: 'uppercase', marginBottom: 8 }}>Nova oportunidade da carteira</div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10, marginBottom: 8 }}>
            <div>
              <div style={lbl}>Cliente</div>
              <input value={`${nome}${telefone ? ' · ' + telefone : ''}`} readOnly style={{ ...inp, background: '#f5f5f5', color: '#555' }} />
            </div>
            <div>
              <div style={lbl}>Origem</div>
              <input value="Carteira" readOnly style={{ ...inp, background: '#f0fdfa', color: COR_ORIGEM.Carteira, fontWeight: 700 }} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 8 }}>
            <div>
              <div style={lbl}>Closer *</div>
              <select value={form.closer} onChange={e => setForm(f => ({ ...f, closer: e.target.value }))} style={inp}>
                <option value="">Selecione...</option>
                {closers.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <div style={lbl}>Etapa</div>
              <select value={form.etapa} onChange={e => setForm(f => ({ ...f, etapa: e.target.value }))} style={inp}>
                {ETAPAS.filter(e => e.key !== 'ganho').map(e => <option key={e.key} value={e.key}>{e.label}</option>)}
              </select>
            </div>
          </div>
          <div style={{ marginBottom: 10 }}>
            <div style={lbl}>O que é o negócio <span style={{ fontWeight: 400, color: '#9ca3af' }}>(marca nova, renovação, outra classe…)</span></div>
            <textarea value={form.observacao} onChange={e => setForm(f => ({ ...f, observacao: e.target.value }))} rows={2}
              placeholder="Ex.: registrar a marca da nova linha de produtos na classe 25"
              style={{ ...inp, height: 'auto', resize: 'vertical', padding: '4px 6px' }} />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={criar} disabled={salvando}
              style={{ background: '#0a1628', color: '#fff', border: 'none', borderRadius: 4, padding: '5px 14px', fontSize: 12, fontWeight: 600, cursor: salvando ? 'wait' : 'pointer' }}>
              {salvando ? 'Criando…' : 'Criar oportunidade'}
            </button>
            <button type="button" onClick={() => setAbrindo(false)}
              style={{ background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 4, padding: '5px 12px', fontSize: 12, cursor: 'pointer' }}>
              Cancelar
            </button>
          </div>
        </div>
      )}

      {erro && <div style={{ color: '#b91c1c', fontSize: 12, marginBottom: 8 }}>{erro}</div>}

      {lista === null ? (
        <div style={{ fontSize: 12.5, color: '#6b7280', padding: 12 }}>Carregando…</div>
      ) : lista.length === 0 ? (
        <div style={{ fontSize: 12.5, color: '#9ca3af', padding: 16, textAlign: 'center' }}>Nenhuma oportunidade ligada a este cliente ainda.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Criada em</th>
                <th style={th}>Negócio</th>
                <th style={th}>Etapa</th>
                <th style={th}>Origem</th>
                <th style={th}>Closer</th>
                <th style={{ ...th, textAlign: 'right' }}>Venda</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {lista.map(op => {
                const et = etapaDe(op.etapa);
                const origem = (op.origem_lead || '').trim();
                const total = totalVenda(op);
                return (
                  <tr key={op._id} onClick={() => navigate(`/crm?oportunidade=${op._id}`)} title="Abrir no CRM"
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = ''; }}>
                    <td style={{ ...td, whiteSpace: 'nowrap', color: '#6b7280' }}>{fmtData(op.criado_em)}</td>
                    <td style={{ ...td, maxWidth: 320 }}>
                      <div style={{ fontWeight: 600, color: '#0a1628' }}>{op.favorita ? '⭐ ' : ''}{op.nome_cliente}</div>
                      {op.observacao && <div style={{ fontSize: 11.5, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{op.observacao}</div>}
                    </td>
                    <td style={td}>
                      <span style={{ background: et.bg, color: et.cor, border: `1px solid ${et.cor}55`, borderRadius: 3, padding: '1px 7px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>{et.label}</span>
                    </td>
                    <td style={td}>
                      {origem
                        ? <span style={{ color: COR_ORIGEM[origem] || '#6b7280', border: `1px solid ${COR_ORIGEM[origem] || '#d1d5db'}`, borderRadius: 3, padding: '1px 6px', fontSize: 10.5, fontWeight: 700, whiteSpace: 'nowrap' }}>{origem}</span>
                        : <span style={{ color: '#c3cad3' }}>—</span>}
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{op.closer || '—'}</td>
                    <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap', color: total > 0 ? '#15803d' : '#c3cad3', fontWeight: total > 0 ? 700 : 400 }}>{total > 0 ? moeda(total) : '—'}</td>
                    <td style={{ ...td, color: '#9ca3af', fontSize: 11, whiteSpace: 'nowrap' }}>abrir no CRM ›</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
